import { useEffect, useContext, useState } from "react";
import { Grid, Radio, RadioGroup, FormControlLabel, FormControl, TextField, Chip, Stack } from "@mui/material";
import styles from "./styles.module.css";
import CustomCheckbox from "components/utils/CustomFields/CustomCheckbox";
import { DASHBOARD_OPTIONS, SYSTEM_VIEW, PRESET_NOTES, CUSTOM_PRODUCT_NOTES_KEY, STORAGE_KEYS } from "const";
import ShopContext from "context/ShopContext";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import ButtonGray from "components/utils/CustomFields/ButtonGray";
import { TerminalContext } from "context/TerminalContext";
import * as ERPHlper from "helpers/ERPHelper";
import * as TerminalHelper from "helpers/TermialHelper";
import * as AppSettings from "helpers/AppSettingsHelper";

function SystemConfigurationPage() {
  const {
    selectedDashboards,
    loading,
    fetchTerminalSettings,
    updateTerminalSettings,
    setTerminalSettings,
    settingsCache,
  } = useContext(TerminalContext);
  const { hasFullKotValidity } = useContext(ShopContext);

  const [selectedView, setSelectedView] = useState(ERPHlper.getSystemView);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [orderTypes, setOrderTypes] = useState(() => TerminalHelper.loadOrderTypesFromStorage() || []);
  const [cloudSyncEnabled, setCloudSyncEnabled] = useState(() => {
    const val = localStorage.getItem(STORAGE_KEYS.CLOUD_SYNC_ENABLED);
    return val !== "false";
  });

  useEffect(() => {
    fetchTerminalSettings();
  }, [fetchTerminalSettings]);

  useEffect(() => {
    setOrderTypes(TerminalHelper.loadOrderTypesFromStorage() || []);
  }, [settingsCache]);

  useEffect(() => {
    const loadNotes = async () => {
      try {
        const storedNotes = await AppSettings.getSetting(CUSTOM_PRODUCT_NOTES_KEY);
        if (storedNotes && Array.isArray(storedNotes)) {
          setNotes(storedNotes);
        } else {
          setNotes(PRESET_NOTES);
          await AppSettings.saveSetting(CUSTOM_PRODUCT_NOTES_KEY, PRESET_NOTES);
        }
      } catch (err) {
        console.error("Failed to load custom notes:", err);
        setNotes(PRESET_NOTES);
      }
    };
    loadNotes();
  }, []);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    const updatedNotes = [...notes, newNote.trim()];
    setNotes(updatedNotes);
    setNewNote("");
    try {
      await AppSettings.saveSetting(CUSTOM_PRODUCT_NOTES_KEY, updatedNotes);
    } catch (err) {
      console.error("Failed to save note:", err);
    }
  };

  const handleDeleteNote = async (indexToDelete) => {
    const updatedNotes = notes.filter((_, idx) => idx !== indexToDelete);
    setNotes(updatedNotes);
    try {
      await AppSettings.saveSetting(CUSTOM_PRODUCT_NOTES_KEY, updatedNotes);
    } catch (err) {
      console.error("Failed to delete note:", err);
    }
  };

  const handleDashboardSelectionToggle = (dashboardName) => {
    setTerminalSettings((prev) => ({
      ...prev,
      selectedDashboards: prev.selectedDashboards.includes(dashboardName)
        ? prev.selectedDashboards.filter((item) => item !== dashboardName)
        : [...prev.selectedDashboards, dashboardName],
    }));
  };

  const handleUpdate = async () => {
    ERPHlper.saveSystemView(selectedView);
    localStorage.setItem(STORAGE_KEYS.CLOUD_SYNC_ENABLED, String(cloudSyncEnabled));

    let dashboardsToSave = selectedDashboards;

    if (selectedView === SYSTEM_VIEW.ERP) {
      dashboardsToSave = [];
    }

    try {
      await AppSettings.saveSetting(CUSTOM_PRODUCT_NOTES_KEY, notes);
    } catch (err) {
      console.error("Failed to save custom notes during update:", err);
    }

    updateTerminalSettings(dashboardsToSave, selectedView);
  };

  return (
    <div className={styles.container}>
      <div className={styles.scrollableArea}>
        <Grid style={{ width: "100%" }}>
          <Grid>
            <h3 className={styles.title}>System View</h3>
            <FormControl component="fieldset" style={{ width: "100%" }}>
              <RadioGroup
                value={selectedView}
                onChange={(e) => setSelectedView(e.target.value)}
                sx={{ flexDirection: "row", flexWrap: "wrap", gap: "10px", padding: "10px 0", width: "100%" }}
              >
                {Object.values(SYSTEM_VIEW).map((pref) => (
                  <FormControlLabel
                    key={pref}
                    value={pref}
                    className={styles.option}
                    control={
                      <Radio
                        sx={{
                          transform: "scale(1.2)",
                          "& .MuiSvgIcon-root": {
                            fontSize: 22,
                          },
                          "&.Mui-checked": {
                            color: "#e50218",
                          },
                          padding: "0 10px 0 0",
                        }}
                      />
                    }
                    label={pref.toUpperCase()}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Grid>
          {/* Dashboards */}
          <Grid item xs={12} className={styles.sectionWrapper}>
            <h3 className={styles.title}>Dashboards</h3>
            {loading ? (
              <SkeletonLoading isOpen={loading} count={2} height={55} xs={3} md={3} sm={3} lg={3} />
            ) : (
              <Grid className={styles.optionsContainer}>
                {Object.entries(DASHBOARD_OPTIONS)
                  .filter(([key]) => {
                    if (key === "TABLE_DASHBOARD" && !hasFullKotValidity) return false;
                    return true;
                  })
                  .map(([key, label]) => (
                    <Grid
                      item
                      key={key}
                      className={styles.option}
                      onClick={() => selectedView !== SYSTEM_VIEW.ERP && handleDashboardSelectionToggle(key)}
                      style={selectedView === SYSTEM_VIEW.ERP ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                    >
                      <div className={`${styles.optionBox} ${selectedDashboards.includes(key) ? styles.active : ""}`}>
                        <CustomCheckbox
                          checked={selectedDashboards.includes(key)}
                          onChange={(e) => e.stopPropagation()}
                          style={{ padding: "0 10px 0 0" }}
                          disabled={selectedView === SYSTEM_VIEW.ERP}
                        />
                        <span className={styles.labelText}>{label}</span>
                      </div>
                    </Grid>
                  ))}
              </Grid>
            )}
          </Grid>

          {/* Order Types */}
          {orderTypes.length > 0 && (
            <Grid item xs={12} className={styles.sectionWrapper}>
              <h3 className={styles.title}>Order Types</h3>
              {loading ? (
                <SkeletonLoading isOpen={loading} count={1} height={45} xs={3} md={3} sm={3} lg={3} />
              ) : (
                <Stack direction="row" flexWrap="wrap" gap="8px" style={{ marginTop: "10px" }}>
                  {orderTypes.map((type, index) => (
                    <Chip
                      key={index}
                      label={type}
                      className={styles.chip}
                      sx={{
                        backgroundColor: "rgba(43, 43, 43, 0.1)",
                        color: "#2b2b2b",
                        fontSize: "15px",
                        fontWeight: 500,
                        borderRadius: "8px",
                        height: "55px",
                        padding: "20px",
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Grid>
          )}

          <Grid item xs={12} className={styles.sectionWrapper}>
            <h3 className={styles.title}>Custom Notes</h3>
            {loading ? (
              <SkeletonLoading isOpen={loading} count={2} height={55} xs={3} md={3} sm={3} lg={3} />
            ) : (
              <Grid container direction="column" gap="15px" style={{ marginTop: "10px" }}>
                <Grid display="flex" gap="10px" alignItems="center">
                  <TextField
                    size="small"
                    placeholder="Enter custom note"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    sx={{
                      flex: 1,
                      maxWidth: "300px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        backgroundColor: "#fff",
                      },
                    }}
                  />
                  <ButtonRed onClick={handleAddNote} style={{ height: "38px" }}>
                    Add
                  </ButtonRed>
                </Grid>
                <Grid>
                  <Stack direction="row" flexWrap="wrap" gap="8px">
                    {notes.map((item, index) => (
                      <Chip
                        key={index}
                        label={item}
                        onDelete={() => handleDeleteNote(index)}
                        sx={{
                          backgroundColor: "rgba(43, 43, 43, 0.1)",
                          color: "#2b2b2b",
                          fontSize: "13px",
                          fontWeight: 500,
                          borderRadius: "8px",
                          "& .MuiChip-deleteIcon": {
                            color: "#e50218",
                            "&:hover": {
                              color: "#c40214",
                            },
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            )}
          </Grid>

          <Grid item xs={12} className={styles.sectionWrapper}>
            <h3 className={styles.title}>Cloud sale request</h3>
            <Grid className={styles.optionsContainer}>
              <Grid item className={styles.option} onClick={() => setCloudSyncEnabled(!cloudSyncEnabled)}>
                <div className={`${styles.optionBox} ${cloudSyncEnabled ? styles.active : ""}`}>
                  <CustomCheckbox
                    checked={cloudSyncEnabled}
                    onChange={(e) => e.stopPropagation()}
                    style={{ padding: "0 10px 0 0" }}
                  />
                  <span className={styles.labelText}>Cloud sale request</span>
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>

      {/* Buttons */}
      <Grid item xs={12} className={styles.buttonContainer}>
        <ButtonGray>Cancel</ButtonGray>
        <ButtonRed onClick={handleUpdate} disabled={loading}>
          Update
        </ButtonRed>
      </Grid>
    </div>
  );
}

export default SystemConfigurationPage;
